import { Breakpoints } from '@angular/cdk/layout';
import { Component, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  isWeekPod,
  WholesaleDataPodsComponent,
} from '@jfw/angular/wholesale/data-pods';
import { DealerDashboardHeroComponent } from '@jfw/angular/wholesale/dealer-dashboard-hero';
import { WholesaleOrderListViewComponent } from '@jfw/angular/wholesale/order-list-view';
import { WholesalePotentialEventsListViewComponent } from '@jfw/angular/wholesale/potential-events-list-view';
import {
  DashboardPod,
  EventListItem,
  DealerPortalModule as Module,
  OrderListItem,
} from '@jfw/types';
import { environment } from 'ecommerce/dealer-portal/src/environments/environment';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  skip,
  switchMap,
} from 'rxjs';
import { BannerService } from '../../services/banner/banner.service';
import { DealerAccountService } from '../../services/dealer-account/dealer-account.service';
import { DealerUserService } from '../../services/dealer-user/dealer-user.service';
import { ModuleAccessService } from '../../services/module-access/module-access.service';
import { AccountInfoDashboardComponent } from '../account-info-dashboard/account-info-dashboard.component';
import { AccountBalanceDeniedModalComponent } from '../modals/account-balance-denied/account-balance-denied.component';
import { ReferralPodsComponent } from '../referral-pods/referral-pods.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    AccountInfoDashboardComponent,
    DealerDashboardHeroComponent,
    ReferralPodsComponent,
    WholesaleOrderListViewComponent,
    WholesaleDataPodsComponent,
    WholesalePotentialEventsListViewComponent,
  ],
})
export class DashboardComponent {
  private readonly router = inject(Router);
  private readonly dealerUserService = inject(DealerUserService);
  private readonly dealerAccountService = inject(DealerAccountService);
  private readonly moduleAccessService = inject(ModuleAccessService);
  private readonly bannerService = inject(BannerService);
  private readonly dialog = inject(MatDialog);

  protected readonly xsBreakpoint = Breakpoints.XSmall;

  protected readonly newSite = toSignal(this.moduleAccessService.newSite$);

  // EXT-6166: which banner a dealer sees depends on their account's
  // ecomStatus, so refetch whenever the current account changes, not just
  // newSite.
  protected readonly bannerResponse = toSignal(
    combineLatest([
      this.moduleAccessService.newSite$,
      this.dealerAccountService.currentDealerAccount$.pipe(
        map((account) => account?.accountNumber),
        distinctUntilChanged(),
      ),
    ]).pipe(
      switchMap(([newSite, accountNumber]) => {
        if (newSite && accountNumber) {
          return this.bannerService.getCurrentBanner();
        }
        return of(null);
      }),
    ),
  );

  protected readonly cmsBanner = computed(() => {
    const response = this.bannerResponse();
    return response?.success ? response.banner : null;
  });

  protected readonly displayDeliveryInfo = toSignal(
    this.moduleAccessService.canAccessModule(Module.DeliveryInfo),
  );

  // Unlike DeliveryInfo (wholesale-only), Announcements is granted to every
  // newSite dealer account regardless of wholesale/referral status -- so the
  // account-info-dashboard block below has to render for those dealers too,
  // even when they'd never see DeliveryInfo/Shipping Info on their own.
  protected readonly displayAnnouncements = toSignal(
    this.moduleAccessService.canAccessModule(Module.Announcements),
  );

  protected readonly displayWholesalePods = toSignal(
    this.moduleAccessService.canAccessModule(Module.WholesalePods),
  );

  protected readonly ecomStatus = toSignal(
    this.dealerAccountService.currentDealerAccount$.pipe(
      map((account) => account?.ecomStatus),
    ),
  );

  protected readonly role = toSignal(
    this.dealerUserService.currentDealer$.pipe(
      map((dealer) => dealer?.role),
    ),
  );

  protected platformOrderBaseUrl = environment.platform_order_base_url;

  protected orderList: OrderListItem[] = [];
  protected eventList: EventListItem[] = [];

  // EXT-6037: This Week/Next Week pods default-sort by order # instead of
  // the table's usual event-date default; every other pod keeps date.
  protected orderListDefaultSort: 'eventDate' | 'orderNumber' = 'eventDate';

  private readonly currentDealerId$ =
    this.dealerUserService.currentDealer$.pipe(
      map((currentDealer) => currentDealer?.dealerId),
    );

  protected readonly currentDealerId = toSignal(this.currentDealerId$);

  constructor() {
    // EXT-6456: the pods collapse themselves when the dealer account changes,
    // but nothing told this page, so the previous account's list stayed on
    // screen. skip(1) leaves the first account alone on a fresh load.
    this.currentDealerId$
      .pipe(distinctUntilChanged(), skip(1), takeUntilDestroyed())
      .subscribe(() => this.clearPodLists());
  }

  protected readonly componentEnvironment = environment.envVersion;

  protected readonly orderSearchApiServerUrl =
    environment.order_search_api_server_url;

  protected readonly platformEventApiServerUrl =
    environment.platform_event_api_server_url;

  protected openBalance(): void {
    if (this.role() === 'Associate') {
      this.dialog.open(AccountBalanceDeniedModalComponent, {
        autoFocus: false,
        maxWidth: '440px',
        width: '440px',
      });
    } else {
      this.router.navigate(['/reports/account-balance']);
    }
  }

  protected onNewItemListEvent(newDashboardPod: DashboardPod | null): void {
    console.log(
      'ParentComponent: Received message from Source:',
      newDashboardPod,
    );

    if (!newDashboardPod) {
      console.warn('Received null DashboardPod');
      this.clearPodLists();
      return;
    }

    // Update the property that is bound to the TargetComponent's input
    if (newDashboardPod.pod === 'POTENTIAL_EVENTS') {
      this.eventList = newDashboardPod.itemList as EventListItem[];
      this.orderList = []; // Clear order list if events are received
    } else {
      this.orderList = newDashboardPod.itemList as OrderListItem[];
      this.eventList = []; // Clear event list if orders are received
      this.orderListDefaultSort = isWeekPod(newDashboardPod)
        ? 'orderNumber'
        : 'eventDate';
    }
  }

  private clearPodLists(): void {
    this.orderList = [];
    this.eventList = [];
    this.orderListDefaultSort = 'eventDate';
  }
}
